import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import axios from "axios";
import ClearDay from "../images/icons/clear-day.png";
import ClearNight from "../images/icons/clear-night.png";
import Rain from "../images/icons/rain.png";
import Snow from "../images/icons/snow.png";
import Sleet from "../images/icons/sleet.png";
import Wind from "../images/icons/wind.png";
import WindLevel2 from "../images/icons/wind_level2.png";
import WindLevel3 from "../images/icons/wind_level3.png";
import WindLevel4 from "../images/icons/wind_level4.png";
import Fog from "../images/icons/fog.png";
import Cloudy from "../images/icons/cloudy.png";
import LocatorIcon from "../images/icons/map-pin.png"
import PartlyCloudyDay from "../images/icons/partly-cloudy-day.png";
import PartlyCloudyNight from "../images/icons/partly-cloudy-night.png";

import ClearDayBackground from "../images/backgrounds/pictures/clear-day-background.jpg"
import ClearNightBackground from "../images/backgrounds/pictures/clear-night-background.jpg"
import CloudyBackground from "../images/backgrounds/pictures/cloudy-background.jpg"
import CloudyNightBackground from "../images/backgrounds/pictures/partly-cloudy-night-background.jpg"
import CloudyDayBackground from "../images/backgrounds/pictures/partly-cloudy-day-background.jpg"
import FoggyBackground from "../images/backgrounds/pictures/foggy-background.jpg"
import RainBackground from "../images/backgrounds/pictures/rain-background.jpg"
import SleetBackground from "../images/backgrounds/pictures/sleet-background.jpg"
import SnowyBackground from "../images/backgrounds/pictures/snow-background.jpg"
import WindyBackground from "../images/backgrounds/pictures/windy-background.jpg"
import WeatherDefault from '../images/backgrounds/pictures/partly-cloudy-day_Background_2.png'

import ArrowRight from '../images/icons/arrow-right.png'
import BlueGlow from '../images/icons/blue-glow.png'
import RedGlow from '../images/icons/red-glow.png'
import BottomGlow from '../images/icons/bottom-glow.png'
import WindDetail from "../images/detail_icons/windDetail.png"
import RainDetail from "../images/detail_icons/rainDetail.png"
import MyRadarLogo from "../images/logo/MyRadar_2020_RGB_Primary_Horizontal_Lockup_Orange_Gray_Logotype_On_Transparent.png";
import "../styles/widget.css";

// Types
interface WeatherData {
  daily: {
    data: DailyForecast[];
  };
  hourly: {
    data: HourlyForecast[];
  };
  timezone: string;
}

interface DailyForecast {
  time: number;
  icon: string;
  windSpeed: number;
  summary: string;
  temperatureHigh: number;
  apparentTemperatureMax: number;
  windBearing: number;
  precipProbability: number;
}

interface HourlyForecast {
  time: number;
  icon: string;
  summary: string;
  windSpeed: number;
  temperature: number;
  apparentTemperature: number;
  precipProbability: number;
  windBearing: number;
}

interface Location {
  lat: number;
  lon: number;
}
type OptionType = {
  value: string | number;
  label: string
};

type WeatherMapping = Record<
  string,
  { icon: string; backgroundImage: string } | undefined
>;

const WeatherWidget: React.FC = () => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [location, setLocation] = useState<Location>({
    lat: 40.7128,
    lon: -74.006,
  });
  const [type, setType] = useState<"daily" | "hourly">("daily");
  const [duration, setDuration] = useState<number>(7);
  const [theme, setTheme] = useState<"premium" | "radar">("premium");
  const [error, setError] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const hasFetched = useRef(false);
  const [page, setPage] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);

  const weatherMapping: WeatherMapping = {
    "clear-day": {
      icon: ClearDay,
      backgroundImage: ClearDayBackground,
    },
    "clear-night": {
      icon: ClearNight,
      backgroundImage: ClearNightBackground,
    },
    rain: {
      icon: Rain,
      backgroundImage: RainBackground,
    },
    snow: {
      icon: Snow,
      backgroundImage: SnowyBackground,
    },
    sleet: {
      icon: Sleet,
      backgroundImage: SleetBackground,
    },
    wind: {
      icon: Wind,
      backgroundImage: WindyBackground,
    },
    fog: {
      icon: Fog,
      backgroundImage: FoggyBackground,
    },
    cloudy: {
      icon: Cloudy,
      backgroundImage: CloudyBackground,
    },
    "partly-cloudy-day": {
      icon: PartlyCloudyDay,

      backgroundImage: CloudyDayBackground,
    },
    "partly-cloudy-night": {
      icon: PartlyCloudyNight,
      backgroundImage: CloudyNightBackground,
    },
  };

  useEffect(() => {
    //This prevent the call being made multiple time once the data is marked as collected
    if (hasFetched.current) return;
    hasFetched.current = true;

    const params = new URLSearchParams(window.location.search);
    console.log("URL Params:", params.toString());
    const latParam = parseFloat(params.get("latitude") || "40.7128"); // Default to New York lat
    const lonParam = parseFloat(params.get("longitude") || "-74.006"); // Default to New York lon
    const typeParam = params.get("type") as "daily" | "hourly";
    const durationParam = parseInt(
      params.get(typeParam === "hourly" ? "hours" : "days") || "7"
    );
    const themeParam = params.get("theme") as "premium" | "radar";
    const apiKeyParam =
      params.get("key") || process.env.REACT_APP_MYRADAR_KEY; // ADD KEY HERE with or operator ||

    const embedUrl = process.env.REACT_APP_EMBED_URL
    if (!apiKeyParam) {
      setError("API key is required");
      return;
    }

    if (!apiKeyParam) {
      setError("API key is required");
      return;
    }

    setApiKey(apiKeyParam);
    setLocation({ lat: latParam, lon: lonParam });
    setType(
      typeParam === "hourly" || typeParam === "daily" ? typeParam : "daily"
    );
    setDuration(
      (typeParam === "hourly" && [6, 12].includes(durationParam)) ||
        (typeParam === "daily" && [3, 5, 7].includes(durationParam))
        ? durationParam
        : 7
    );
    // This is the premium theme
    //setTheme(themeParam === "radar" ? "radar" : "premium");

    //This is the Radar theme
    setTheme(themeParam === "premium" ? "premium" : "radar");
    console.log("Theme from URL:", themeParam);

    const fetchWeather = async () => {
      try {
        const url = `${embedUrl}/forecast/data/${latParam}/${lonParam}?subscription_key=${apiKeyParam}`;
        const response = await axios.get<WeatherData>(url);
        setWeather(response.data);
      } catch (err) {
        setError("Could not fetch weather data");
        console.error(err);
      }
    };

    fetchWeather();
  }, []);

  /****************************/
  //Testing Dropdown functions

  const typeOptions: OptionType[] = [
    { value: "daily", label: "Daily" },
    { value: "hourly", label: "Hourly" },
  ];

  const durationOptions: OptionType[] = type === "daily"
    ? [
      { value: 3, label: "3 Day" },
      { value: 5, label: "5 Day" },
      { value: 7, label: "7 Day" },
    ]
    : [
      { value: 6, label: "6 Hours" },
      { value: 12, label: "12 Hours" },
    ];


  const handleTypeChange = (selectedOption: { value: "daily" | "hourly" }) => {
    setType(selectedOption.value);
    setDuration(selectedOption.value === "hourly" ? 6 : 7); // Set default duration
  };

  const handleDurationChange = (selectedOption: { value: number }) => {
    setDuration(selectedOption.value);
  };

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: "20px",
      height: "18px",
      width: "clamp(58px, 10vw, 72px)",

      fontSize: "clamp(8px, 1vw, 12px)",
      borderRadius: "10px !important",
      zIndex: 2,
      transition: "all 0.3s ease",
      boxShadow: "0px"
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      "svg": { width: "clamp(10px, 1.5vw, 12px)", height: "clamp(8px, 1.5vw, 12px)" },
      paddingTop: 0,
      paddingBottom: 0,
      height: "14px",
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: "0px 4px",
      justifyContent: "center",
      minHeight: "17px",
      display: "flex",
      alignItems: "center",
    }),
    singleValue: (provided: any) => ({
      ...provided,
      fontSize: "clamp(6px, 1vw, 12px)",
      lineHeight: "normal",
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      marginTop: "1px",

    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      width: "0px"
    }),
    menu: (provided: any) => ({
      ...provided,
      fontSize: "clamp(8px, 1vw, 12px)",
      zIndex: 10,
      marginTop: "0px",
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    option: (provided: any, state: any) => ({
      ...provided,
      fontSize: "clamp(6px, 1vw, 12px)",
      padding: "2px 6px",
      backgroundColor: state.isSelected ? "#007bff" : "white",
      color: state.isSelected ? "white" : "black",
      "&:hover": { backgroundColor: "#f0f0f0" },
    }),
  };
  /* End of Dropdown function */
  /****************************/

  const getDayName = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    const today = new Date();
    if (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    ) {
      return "Today";
    }
    return date.toLocaleDateString("en-US", { weekday: "long" });
  };

  //Summary Sentence split
  const getSummarySentence = (text: string): string => {
    const match = text.match(/(.*?)[.!?]\s|(.+)[.!?]$/);
    return match ? (match[1] || match[2]) : text;
  };

  //Wind leveling Icons
  function getWindIcon(windSpeed: number): string {
    if (windSpeed <= 10) return Wind;
    if (windSpeed <= 15) return WindLevel2;
    if (windSpeed <= 20) return WindLevel3;
    return WindLevel4;
  }

  function degToCompass(num: number): string {
    const val = Math.floor(num / 22.5 + 0.5);
    const directions = [
      "N", "NE", "E", "SE",
      "S", "SW", "W", "NW",
    ];
    return directions[val % 8];
  }

  /*Pagination for the 12 hour display */
  const handleScroll = () => {
    if (isFlipped) {
      setPage((prev) => Math.max(prev - 1, 0)); // Scroll left
    } else {
      setPage((prev) => Math.min(prev + 1, 1)); // Scroll right
    }
    setIsFlipped(!isFlipped); // Toggle direction
  };


  // Calculate start and end indices for pagination (only for 12-hour forecast)
  const startIndex = type === "hourly" && duration === 12 ? page * 6 : 0;
  const endIndex = type === "hourly" && duration === 12 ? startIndex + 6 : duration;

  const isDisabled = (isFlipped && page === 0) || (!isFlipped && page >= 1);


  if (error) {
    return <div className="widget-error">{error}</div>;
  }

  if (!weather) {
    return <div className="widget-loading">Loading...</div>;
  }

  const currentWeather = weather.daily?.data[0];
  const currentMapping =
    currentWeather && weatherMapping[currentWeather.icon]
      ? weatherMapping[currentWeather.icon]
      : { backgroundImage: WeatherDefault };

  return (
    <div
      className="weather-widget"
    >
      {theme === "radar" ? (
        <div className="weather-background-radar"></div>
      ) : (
        <img
          alt="weather-background"
          className="weather-background"
          src={`${currentMapping?.backgroundImage}?timestamp=${Date.now()}`}
        />
      )}
      <div className="widget-header">
        <div className="logo-box" style={{ maxWidth: "250px", width: "20%", margin: "5px auto", marginLeft: "0", display: "block" }}>
          <img
            style={{ width: "100%", height: "auto" }}
            className="myradar-logo"
            src={MyRadarLogo}
            alt="MyRadar Logo"
          />
        </div>
        <div className="radar-tagline">
          <p className="widget-location">Keeping you ahead of the storm</p>
        </div>
        <div className="location-selector">
          <div className="location-parent" onClick={() => setIsOpen(!isOpen)}>
            <img alt="locator-icon" className="locator-icon" src={LocatorIcon} />
            <p className="widget-location">{weather.timezone}</p>
            <div className={`testing-dropdowns ${isOpen ? "open" : ""}`} onClick={(e) => e.stopPropagation()}>
              <Select
                options={typeOptions}
                value={typeOptions.find((option) => option.value === type)}
                onChange={(selectedOption) => {
                  if (selectedOption) handleTypeChange(selectedOption as { value: "daily" | "hourly" });
                }}
                styles={customStyles}
                className="custom-select"
                classNamePrefix="custom"
                isSearchable={false}
                menuPortalTarget={document.body}
              />
              <Select
                options={durationOptions}
                value={durationOptions.find((option) => option.value === duration)}
                onChange={(selectedOption) => {
                  if (selectedOption) handleDurationChange(selectedOption as { value: number });
                }}
                styles={customStyles}
                className="custom-select"
                classNamePrefix="custom"
                isSearchable={false}
                menuPortalTarget={document.body}
              />

            </div>
          </div>
          {type === "hourly" && duration === 12 && (
            <div className="arrow-button">
              {theme === "radar" && (
                <div className="arrow-glow-background">
                  <img
                    className="red-glow"
                    src={RedGlow}
                    alt="glow"
                  />
                  <img
                    className="blue-glow"
                    src={BlueGlow}
                    alt="glow"
                  />
                </div>
              )}
              <button
                onClick={handleScroll}
                className={`arrow ${isFlipped ? "flipped" : ""} ${isDisabled ? "disabled" : ""}`}
                disabled={isDisabled}
              >
                <img
                  src={ArrowRight}
                  alt="Toggle Arrow"
                  className="arrow-img"
                />
              </button>
            </div>
          )}
        </div>
      </div>
      {type === "daily" ? (
        <div className={`widget-daily ${theme === "radar" ? "radar-theme" : ""}`}>
          {weather.daily.data.slice(0, duration).map((day, index) => {
            const windDirection = degToCompass(day.windBearing); // Convert wind bearing to direction
            const dayName = getDayName(day.time); // Get the day name
            const isWeekend = dayName === "Saturday" || dayName === "Sunday";
            return (
              <div key={index} className="daily-weather">
                <div className={`day-header ${isWeekend ? "weekend-header" : ""}`}>
                  <h4 className="widget-day">{dayName}</h4>
                </div>
                <div className="daily-content">
                  <img
                    className="widget-icon"
                    src={
                      day.icon === "wind"
                        ? getWindIcon(day.windSpeed) // Replace only if the icon is wind
                        : weatherMapping[day.icon]?.icon // Use default mapping otherwise
                    }
                    alt={day.icon}
                    width={50}
                    height={50}
                  />
                  <p className="widget-temp-high">{Math.round(day.temperatureHigh)}°</p>
                  <p className="widget-feels-like-high">Feels like {Math.round(day.apparentTemperatureMax)}°</p>
                  <p className="widget-summary">{getSummarySentence(day.summary)}</p>
                  <div className="widget-windRain-box">
                    <div className="wind-box">
                      <img className="wind-detail-icon" src={WindDetail} alt="wind-detail-icon" />
                      {/* The multiplication of 2.23694 is to convert m/s to MPH */}
                      <p className="widget-wind-direction">{windDirection} {Math.round(day.windSpeed * 2.23694)} MPH</p>
                    </div>
                    <div className="precip-box" >
                      <img className="rain-detail-icon" src={RainDetail} alt="rain-detail-icon" />
                      <p className="widget-precip-percent">{Math.round(day.precipProbability * 100)}%</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={`widget-hourly ${theme === "radar" ? "radar-theme" : ""}`}>
          {weather.hourly.data.slice(startIndex, endIndex).map((hour, index) => {
            const windDirection = degToCompass(hour.windBearing); // Convert wind bearing to direction
            return (
              <div key={index} className="hourly-weather">
                <div className="day-header">
                  <h4 className="widget-hour">
                    {new Date(hour.time * 1000).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: 'numeric',
                      hour12: true,
                    })}
                  </h4>
                </div>
                <div className="hourly-content">
                  <img
                    className="widget-icon"
                    src={
                      hour.icon === "wind"
                        ? getWindIcon(hour.windSpeed) // Replace only if the icon is wind
                        : weatherMapping[hour.icon]?.icon // Use default mapping otherwise
                    }
                    alt={hour.icon}
                    width={50}
                    height={50}
                  />
                  <p className="widget-temp">{Math.round(hour.temperature)}°F</p>
                  <p className="widget-feels-like">
                    Feels like: {Math.round(hour.apparentTemperature)}°
                  </p>
                  <p className="widget-summary">{getSummarySentence(hour.summary)}</p>

                  <div className="widget-windRain-box">
                    <div className="wind-box">
                      <img className="wind-detail-icon" src={WindDetail} alt="wind-detail-icon" />

                      <p className="widget-wind-direction">{windDirection} {Math.round(hour.windSpeed * 2.23694)} MPH</p>
                    </div>
                    <div className="precip-box">
                      <img className="rain-detail-icon" src={RainDetail} alt="rain-detail-icon" />
                      <p className="widget-precip-percent">{Math.round(hour.precipProbability * 100)}%</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {theme === "radar" && (
        <img
          className="bottom-right-corner"
          src={BottomGlow}
          alt="glow"
        />
      )}
    </div>
  );
};

export default WeatherWidget;
